// External library imports
import { Drawer } from 'antd';
import 'antd/dist/antd.min.css';
import axios from 'axios';
import React, { createContext, useContext, useEffect, useState } from 'react';
import CookieConsent from 'react-cookie-consent';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/customToast.css';

// Socket.IO service import
import socketService from './services/socketService';

// Local imports
import { Alert } from './assets';
import {
  AuthenticatedRoute,
  MainHeader,
  PageHeader,
  SpotifyConnectionBanner,
} from './components';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './components/ErrorBoundary/ErrorFallback';
import { InnerPageLayoutStyle1 } from './components/InnerPageLayout/InnerPageLayout.style';
import { LoginForm } from './components/LoginForm';
import { Policies } from './components/PolicyModal';
import {
  SPOTIFY_CODE,
  SPOTIFY_RECONNECT_NOTICE,
  SPOTIFY_TOKEN,
  SPOTIFY_USER_INFO,
} from './constants/storage';
import { ENABLE_CURATOR_EARNINGS_DASHBOARD, ENABLE_CURATOR_WALLET } from './constants/common';
import {
  LoginContextProvider,
  useLoginContext,
  ScheduledSongsProvider,
  PricingProvider,
} from './context';
import { usePackageContext } from './context/PackageContext';
import { PlaylistContextProvider } from './context/PlaylistContext';
import { PackageContextProvider } from './context/PackageContext';
import { CsvImportProvider } from './context/CsvImportContext';
import './index.css';
import {
  Callback,
  CancelSub,
  FuturePlaylist,
  landingPage as LandingPage,
  Search,
  Setting,
  ShiftPage,
  SignupCallback,
} from './pages';
import { Support } from './pages/Support';
import ScheduledTracks from './pages/Future_Playlist/ScheduledTracks';
import { LoginStyle } from './pages/Login/Login.style';
import UserGuide from './pages/UserGuide/UserGuide';
import ShareDetailPage from './pages/landingPage/ShareDetailPage';
import CampaignsPage from './pages/Campaigns/CampaignsPage';
import CuratorOffersPage from './pages/CuratorOffers/CuratorOffersPage';
import CuratorEarningsPage from './pages/Earnings/CuratorEarningsPage';
import CuratorWalletPage from './pages/Wallet/CuratorWalletPage';
import PlaylistNew from './pages/Playlist/PlaylistNew';
import InviteLandingPage from './pages/Invite/InviteLandingPage';
import InviteClaimCoordinator from './components/Invite/InviteClaimCoordinator';
import BaseStyle from './styles/Base.style';
import { GlobalThemeProvider } from './theme/themeProvider';
import {
  Auth0Logout,
  addUserProfileAction,
  deleteItemFromStorage,
  getItemFromStorage,
  isEmptyOrNil,
} from './utils';
import SideBarV2 from './components/SideBar/SideBarV2';
import HomeV2 from './pages/Home/HomeV2';
import ErrorTest from './components/ErrorTest';

// Development-only testing utilities (removed for production)

// TypeScript declarations for global functions
declare global {
  interface Window {
    clarityInit?: () => void;
    gtag?: (command: string, targetId: string, config?: any) => void;
  }
}

window.Buffer = window.Buffer || require('buffer').Buffer;

interface SidebarContextType {
  showSidebar: boolean;
  setShowSidebar: React.Dispatch<React.SetStateAction<boolean>>;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const SidebarProvider: React.FC = ({ children }) => {
  const [showSidebar, setShowSidebar] = useState(true);

  return (
    <SidebarContext.Provider value={{ showSidebar, setShowSidebar }}>
      {children}
    </SidebarContext.Provider>
  );
};
type sideBarProp = {
  classname?: string;
};
const SideBar = (props: sideBarProp) => {
  const { showSidebar } = useContext(SidebarContext) as SidebarContextType;

  return showSidebar ? <SideBarV2 className={props.classname} /> : null;
};
const Layout: React.FC = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [isSpotifyConnected, setIsSpotifyConnected] = useState(false);

  useEffect(() => {
    const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser'));
    const hasConnection = localStorage.getItem('hasSpotifyConnection') === 'true';
    setIsSpotifyConnected(!!(spotifyUser || hasConnection));
  }, []);

  const onClose = () => {
    setOpen(false);
  };

  return (
    <>
      <SpotifyConnectionBanner />
      <MainHeader />
      <InnerPageLayoutStyle1
        className={`inner-page-layout-style1 ${!isSpotifyConnected ? 'no-spotify-padding' : ''}`}
      >
        <SideBar classname={'sidebarForPc'} />
        <div className="page-content" id="page-content">
          {children}
        </div>

        <Drawer
          className="mainSideBar"
          title={false}
          placement="left"
          onClose={onClose}
          open={open}
        >
          <SideBar classname={'sidebarForMob'} />
        </Drawer>
      </InnerPageLayoutStyle1>
    </>
  );
};

const RoutesList = () => {
  const { dispatch } = useLoginContext();
  const { clearCache } = usePackageContext();
  const expired = false;

  // Handle URL parameters for landing page actions
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');

    if (action === 'openLoginOrRedirectToDashboard') {
      // Check if user is already logged in
      const token = localStorage.getItem('userInfo');
      const spotifyUser = localStorage.getItem('spotifyUser');

      if (token && spotifyUser) {
        // User is logged in, redirect to dashboard
        window.location.href = '/home';
      } else {
        // User is not logged in, redirect to landing page with login modal open
        window.location.href = '/?openLogin=true';
      }
    }
  }, []);

  useEffect(() => {
    // Callback route only persists the auth code and redirects here.
    // Exchange must run once on a real app route to avoid aborted requests.
    if (window.location.pathname === '/callback') {
      return;
    }

    let rawCode = getItemFromStorage(SPOTIFY_CODE);
    const code = rawCode?.split('#')[0];
    const codeTimestamp = getItemFromStorage('SPOTIFY_CODE_TIMESTAMP');

    if (isEmptyOrNil(code)) return;

    // Smart validation: Check if code is fresh
    if (codeTimestamp) {
      const age = Date.now() - parseInt(codeTimestamp);
      const MAX_CODE_AGE = 600000; // 10 minutes in milliseconds

      if (age > MAX_CODE_AGE) {
        deleteItemFromStorage(SPOTIFY_CODE);
        deleteItemFromStorage('SPOTIFY_CODE_TIMESTAMP');
        return;
      }
    } else {
      // No timestamp = old code from database, don't use
      deleteItemFromStorage(SPOTIFY_CODE);
      return;
    }

    // Consume the one-time auth code immediately so React remounts cannot double-exchange it.
    deleteItemFromStorage(SPOTIFY_CODE);
    deleteItemFromStorage('SPOTIFY_CODE_TIMESTAMP');

    try {
      const existingLock = sessionStorage.getItem('SPOTIFY_CODE_EXCHANGE_LOCK');
      if (existingLock && existingLock === code) {
        return;
      }
      sessionStorage.setItem('SPOTIFY_CODE_EXCHANGE_LOCK', code);
    } catch {
      // ignore storage failures
    }

    if (code) {
      axios
        .post(
          `${process.env.REACT_APP_BACKEND_URI}/api/spotify/token`,
          {
            code: code,
            redirect_uri: process.env.REACT_APP_REDIRECT_URI,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
        .then(async tokenResponse => {
          try {
            const meResponse = await axios('https://api.spotify.com/v1/me', {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${tokenResponse.data.access_token}`,
              },
            });

            const {
              getOAuthIntent,
              clearOAuthIntent,
              markSpotifyConnected,
              storeSpotifyTokens,
              shouldUseReconnectFlow,
              resolveReconnectIdentity,
              SPOTIFY_PROFILE_MISMATCH_CODE,
              SPOTIFY_REFRESH_MISSING_CODE,
              MISSING_REFRESH_USER_MESSAGE,
            } = await import('./utils/spotifyAuth');
            const { message } = await import('antd');
            const { BASE_URL } = await import('./constants/api-urls');

            const authorizedId = meResponse?.data?.id;
            const displayName = meResponse?.data?.display_name || 'Spotify';
            const imageUrl =
              meResponse?.data?.images?.[0]?.url ||
              meResponse?.data?.image?.[0]?.url ||
              'dummy';
            const accessToken = tokenResponse.data.access_token;
            const refreshToken = tokenResponse.data.refresh_token;
            const userId = JSON.parse(localStorage.getItem('userInfo') || '{}')?.userId;
            const intent = getOAuthIntent();
            // Intent fields belong to one profile — keep them atomic (no mix with another profile).
            const intentIdentity =
              intent?.mode === 'reconnect'
                ? {
                    profileId: intent.targetProfileId,
                    spotifyID: intent.expectedSpotifyId || null,
                    username: intent.expectedDisplayName,
                  }
                : null;
            const reconnectIdentity = resolveReconnectIdentity(intentIdentity);

            const finishRedirect = (path = '/home') => {
              try {
                sessionStorage.removeItem('SPOTIFY_CODE_EXCHANGE_LOCK');
              } catch {
                // ignore
              }
              let redirectPath = path;
              try {
                const stored = localStorage.getItem('postSpotifyRedirect');
                if (stored && stored.startsWith('/') && !stored.startsWith('//')) {
                  redirectPath = stored;
                  localStorage.removeItem('postSpotifyRedirect');
                }
              } catch {
                // keep path
              }
              window.location.replace(redirectPath);
            };

            const returnToReconnect = (notice: string) => {
              localStorage.setItem('SPOTIFY_NEEDS_RECONNECT', 'true');
              localStorage.setItem(SPOTIFY_RECONNECT_NOTICE, notice);
              // Always restore the *intended* reconnect target atomically from OAuth intent.
              const failedTarget =
                intent?.mode === 'reconnect'
                  ? {
                      profileId: intent.targetProfileId,
                      spotifyID: intent.expectedSpotifyId || null,
                      username: intent.expectedDisplayName,
                    }
                  : reconnectIdentity;
              if (failedTarget?.profileId || failedTarget?.username) {
                localStorage.setItem(
                  'SPOTIFY_RECONNECT_PROFILE',
                  JSON.stringify(failedTarget)
                );
                if (failedTarget.profileId) {
                  localStorage.setItem(
                    'SPOTIFY_ACTIVE_PROFILE_ID',
                    String(failedTarget.profileId)
                  );
                }
              }
              clearOAuthIntent();
              finishRedirect('/home');
            };

            // Guarded reconnect for current/expired profile only.
            // Never fall through to Add account on reconnect failures.
            if (shouldUseReconnectFlow(intent)) {
              // Prefer OAuth intent fields (set when user clicked Reconnect on a specific profile).
              const expectedId =
                intent?.expectedSpotifyId || reconnectIdentity?.spotifyID || undefined;
              const targetName =
                intent?.expectedDisplayName ||
                reconnectIdentity?.username ||
                'this profile';
              const targetProfileId =
                intent?.targetProfileId || reconnectIdentity?.profileId || undefined;

              if (expectedId && authorizedId && expectedId !== authorizedId) {
                returnToReconnect(
                  `You connected as ${displayName}, but ${targetName} still needs reconnect. Log into Spotify as ${targetName}, then try Reconnect again. No Songplace profile was changed.`
                );
                return;
              }

              if (!refreshToken) {
                returnToReconnect(MISSING_REFRESH_USER_MESSAGE);
                return;
              }

              try {
                const reconnectRes = await axios.post(`${BASE_URL}/user/reconnect-spotify`, {
                  targetProfileId,
                  expectedSpotifyId: expectedId || authorizedId,
                  spotifyID: authorizedId,
                  username: displayName,
                  image: imageUrl,
                  code,
                  token: accessToken,
                  refresh: refreshToken,
                  userId,
                });

                if (
                  reconnectRes?.data?.success === false ||
                  reconnectRes?.data?.code === SPOTIFY_PROFILE_MISMATCH_CODE ||
                  reconnectRes?.data?.code === SPOTIFY_REFRESH_MISSING_CODE
                ) {
                  returnToReconnect(
                    reconnectRes?.data?.message ||
                      `Could not reconnect ${targetName}. Please try Reconnect again.`
                  );
                  return;
                }

                storeSpotifyTokens(accessToken, refreshToken, tokenResponse.data.expires_in);
                const identity = reconnectRes?.data?.identity || {
                  profileId: targetProfileId,
                  spotifyID: authorizedId,
                  username: displayName,
                };
                const profileId =
                  identity._id || identity.profileId || targetProfileId;

                localStorage.setItem(
                  'spotifyUser',
                  JSON.stringify({
                    username: displayName,
                    image: imageUrl,
                    code,
                    token: accessToken,
                    refresh: refreshToken,
                    spotify_user_id: authorizedId,
                    spotifyID: authorizedId,
                    profileId,
                  })
                );

                markSpotifyConnected({
                  profileId,
                  spotifyID: authorizedId,
                  username: displayName,
                  image: imageUrl,
                });

                dispatch({
                  type: 'SPOTIFY_CONNECTED',
                  payload: {
                    token: accessToken,
                    refreshToken,
                    profileId,
                  },
                });
                dispatch({
                  type: 'ADD_APP_TOKEN',
                  payload: { token: accessToken, refreshToken },
                });

                clearOAuthIntent();
                message.success(
                  `${displayName} is connected again. Your playlists should load shortly.`
                );
                setTimeout(() => finishRedirect('/home'), 600);
                return;
              } catch (reconnectError: any) {
                const reconnectFailure = reconnectError?.response?.data;
                console.error('Guarded reconnect failed:', {
                  status: reconnectError?.response?.status,
                  code: reconnectFailure?.code,
                  message: reconnectFailure?.message || reconnectError?.message,
                });
                returnToReconnect(
                  reconnectFailure?.message ||
                    `We could not reconnect ${targetName}. Please try Reconnect again.`
                );
                return;
              }
            }

            // Add account / first connect only.
            if (!refreshToken) {
              localStorage.setItem(SPOTIFY_RECONNECT_NOTICE, MISSING_REFRESH_USER_MESSAGE);
              clearOAuthIntent();
              finishRedirect('/home');
              return;
            }

            const profileResponse = await addUserProfileAction(
              displayName,
              authorizedId,
              imageUrl,
              code,
              accessToken,
              refreshToken,
              userId
            );
            const savedProfile = profileResponse?.data?.identity;
            const data = {
              username: displayName,
              image: imageUrl,
              code: code,
              token: accessToken,
              refresh: refreshToken,
              spotify_user_id: authorizedId,
              spotifyID: authorizedId,
              profileId: savedProfile?._id || savedProfile?.profileId,
            };
            localStorage.setItem('spotifyUser', JSON.stringify(data, null, ' '));
            localStorage.setItem('hasSpotifyConnection', 'true');

            storeSpotifyTokens(accessToken, refreshToken, tokenResponse.data.expires_in);
            markSpotifyConnected({
              profileId: savedProfile?._id || savedProfile?.profileId,
              spotifyID: authorizedId,
              username: displayName,
              image: imageUrl,
            });

            dispatch({
              type: 'ADD_APP_TOKEN',
              payload: {
                token: accessToken,
                refreshToken: refreshToken,
              },
            });
            dispatch({
              type: 'SPOTIFY_CONNECTED',
              payload: {
                token: accessToken,
                refreshToken,
                profileId: savedProfile?._id || savedProfile?.profileId,
              },
            });

            clearOAuthIntent();
            message.success(`${displayName} is connected.`);
            setTimeout(() => finishRedirect('/home'), 600);
          } catch (error) {
            console.error('Spotify connection failed:', error);
            localStorage.setItem(
              SPOTIFY_RECONNECT_NOTICE,
              'Spotify could not be connected. Please try again from Songplace.'
            );
            try {
              sessionStorage.removeItem('SPOTIFY_CODE_EXCHANGE_LOCK');
            } catch {
              // ignore
            }
            window.location.replace('/home');
          }
        })
        .catch(async error => {
          // Authorization-code failures must NOT invalidate an existing refresh token
          console.error('Spotify token exchange failed:', error);
          const spotifyError = error?.response?.data?.error;
          const detail =
            typeof spotifyError === 'object'
              ? spotifyError?.error_description || spotifyError?.error
              : null;
          localStorage.setItem(
            SPOTIFY_RECONNECT_NOTICE,
            detail
              ? `Spotify authorization failed (${detail}). Please click Reconnect and try again.`
              : 'Spotify authorization failed. Please click Reconnect and try again.'
          );
          try {
            sessionStorage.removeItem('SPOTIFY_CODE_EXCHANGE_LOCK');
          } catch {
            // ignore
          }
          // If already needs reconnect, keep that state; otherwise just send home with notice.
          window.location.replace('/home');
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔔 Socket.IO connection management - MOVED BEFORE return
  useEffect(() => {
    const userInfo = localStorage.getItem('userInfo');

    // Socket.IO should connect if user is authenticated (regardless of Spotify connection)
    if (userInfo) {
      try {
        const user = JSON.parse(userInfo);

        if (user?.userId) {
          // User is authenticated, connect to Socket.IO
          socketService.connect(user.userId, user.idToken || 'fallback-token');

          // Make socketService available globally for debugging
          (window as any).socketService = socketService;
        }
      } catch (error) {
        console.error('🔔 [App.tsx] Failed to parse userInfo:', error);
      }
    } else {
      // User is not authenticated, disconnect Socket.IO
      socketService.disconnect();
    }

    // Cleanup on component unmount
    return () => {
      socketService.disconnect();
    };
  }, []); // Empty dependency array - run once on mount

  const handleLogout = async () => {
    // Disconnect Socket.IO first
    socketService.disconnect();

    try {
      const { clearAllSpotifyBrowserState } = await import('./utils/spotifyAuth');
      clearAllSpotifyBrowserState();
    } catch {
      deleteItemFromStorage(SPOTIFY_TOKEN);
      deleteItemFromStorage(SPOTIFY_CODE);
      deleteItemFromStorage(SPOTIFY_USER_INFO);
      localStorage.removeItem('spotifyUser');
    }
    localStorage.removeItem('userInfo');

    // Clear package cache
    clearCache();

    Auth0Logout();

    dispatch({
      type: 'SET_LOGIN',
      payload: false,
    });
  };

  return (
    <>
      {!expired ? (
        <BrowserRouter>
          <SidebarProvider>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <InviteClaimCoordinator />
              <Routes>
                <Route path="/shift" element={<ShiftPage />} />
                <Route path="/login" element={<LoginForm />} />
                <Route path="/cancel" element={<CancelSub />} />
                <Route path="/error-test" element={<ErrorTest />} />
                <Route path="/invite/:token" element={<InviteLandingPage />} />
                <Route path="/invite" element={<InviteLandingPage />} />
                <Route
                  path="/"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <LandingPage />
                    </AuthenticatedRoute>
                  }
                />
                <Route path="/track-detail" element={<ShareDetailPage />} />
                <Route path="/callback" element={<Callback />} />
                <Route path="/created" element={<SignupCallback />} />

                <Route
                  path="/dashboard"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <Search />
                    </AuthenticatedRoute>
                  }
                />
                <Route
                  path="/future_playlist"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <Layout>
                        <FuturePlaylist />
                      </Layout>
                    </AuthenticatedRoute>
                  }
                />
                <Route
                  path="/schedule_tracks"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <Layout>
                        <ScheduledTracks />
                      </Layout>
                    </AuthenticatedRoute>
                  }
                />

                <Route
                  path="/campaigns"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <Layout>
                        <CampaignsPage />
                      </Layout>
                    </AuthenticatedRoute>
                  }
                />

                <Route
                  path="/curator-offers"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <Layout>
                        <CuratorOffersPage />
                      </Layout>
                    </AuthenticatedRoute>
                  }
                />

                <Route
                  path="/earnings"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      {ENABLE_CURATOR_EARNINGS_DASHBOARD ? (
                        <Layout>
                          <CuratorEarningsPage />
                        </Layout>
                      ) : (
                        <Navigate to="/home" replace />
                      )}
                    </AuthenticatedRoute>
                  }
                />
                <Route
                  path="/wallet"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      {ENABLE_CURATOR_WALLET ? (
                        <Layout>
                          <CuratorWalletPage />
                        </Layout>
                      ) : (
                        <Navigate to="/home" replace />
                      )}
                    </AuthenticatedRoute>
                  }
                />

                <Route
                  path="/search"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <Layout>
                        <Search />
                      </Layout>
                    </AuthenticatedRoute>
                  }
                />

                <Route
                  path="/playlist"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <Layout>
                        <PlaylistNew />
                      </Layout>
                    </AuthenticatedRoute>
                  }
                />

                <Route
                  path="/playlist/:playlistId"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <Layout>
                        <PlaylistNew />
                      </Layout>
                    </AuthenticatedRoute>
                  }
                />

                <Route
                  path="/settings"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <Layout>
                        <Setting />
                      </Layout>
                    </AuthenticatedRoute>
                  }
                />

                <Route
                  path="/home"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <Layout>
                        <HomeV2 />
                      </Layout>
                    </AuthenticatedRoute>
                  }
                />
                <Route
                  path="/user-guide"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <Layout>
                        <UserGuide />
                      </Layout>
                    </AuthenticatedRoute>
                  }
                />
                <Route
                  path="/support"
                  element={
                    <AuthenticatedRoute redirectTo="/">
                      <Layout>
                        <Support />
                      </Layout>
                    </AuthenticatedRoute>
                  }
                />
                <Route
                  path="*"
                  element={
                    <AuthenticatedRoute redirectTo="/home">
                      <Layout>
                        <HomeV2 />
                      </Layout>
                    </AuthenticatedRoute>
                  }
                />
              </Routes>
            </ErrorBoundary>
          </SidebarProvider>
        </BrowserRouter>
      ) : (
        <LoginStyle>
          <div className="background-col-1">
            <div className="background">
              <div className="expire-div" style={{}}>
                <Alert />
              </div>
              <h3 style={{}} className="h3">
                The session has been expired.
              </h3>
              <p className="ppp" style={{}}>
                Please relogin to continue using Songplace.
              </p>
              <div className="dives">
                <button onClick={handleLogout} className="btn1">
                  Logout
                </button>
              </div>
            </div>
          </div>
        </LoginStyle>
      )}
    </>
  );
};

function App() {
  const [isPrivacyModalVisible, setIsPrivacyModalVisible] = React.useState(false);
  const [modalType, setModalType] = React.useState<"policy" | "condition">("policy");

  const handleOpenPrivacyModal = () => {
    setModalType('policy');
    setIsPrivacyModalVisible(true);
  };

  const handleClosePrivacyModal = () => {
    setIsPrivacyModalVisible(false);
  };

  return (
    <div className="App">
      <PageHeader
        title="Songplace - The best platform to manage your Spotify playlists"
        description="The best platform to manage your Spotify playlists."
        image="./static/mini-logo.png"
      />

      <LoginContextProvider>
        <PlaylistContextProvider>
          <PricingProvider>
            <PackageContextProvider>
              <CsvImportProvider>
                <ScheduledSongsProvider>
                  <GlobalThemeProvider>
                    <ToastContainer
                      position="bottom-right"
                      autoClose={7000}
                      pauseOnHover
                      closeOnClick
                    />
                    <BaseStyle />
                    <RoutesList />
                    <CookieConsent
                      location="bottom"
                      buttonText="Accept All"
                      declineButtonText="Decline"
                      cookieName="songplaceCookieConsent"
                      style={{ background: '#111823' }}
                      buttonStyle={{
                        color: 'white',
                        fontSize: '13px',
                        background: '#0059d1',
                      }}
                      declineButtonStyle={{
                        color: 'white',
                        fontSize: '13px',
                        background: '#242A34',
                        marginRight: '10px',
                      }}
                      expires={150}
                      onAccept={() => {
                        localStorage.setItem('cookieConsent', 'accepted');
                        // Initialize Microsoft Clarity after consent
                        if (window.clarityInit) {
                          window.clarityInit();
                        }
                        // Update Google Analytics consent
                        if (window.gtag) {
                          window.gtag('consent', 'update', {
                            analytics_storage: 'granted',
                            ad_storage: 'granted',
                          });
                        }
                      }}
                      onDecline={() => {
                        localStorage.setItem('cookieConsent', 'declined');
                        // Update Google Analytics consent to denied
                        if (window.gtag) {
                          window.gtag('consent', 'update', {
                            analytics_storage: 'denied',
                            ad_storage: 'denied',
                          });
                        }
                      }}
                      enableDeclineButton={true}
                    >
                      This website uses cookies to enhance the user experience and provide
                      analytics. We use Microsoft Clarity for website analytics and Google Analytics
                      for performance monitoring.{' '}
                      <span
                        onClick={handleOpenPrivacyModal}
                        style={{
                          color: '#0059d1',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        Learn more
                      </span>
                    </CookieConsent>
                    <Policies
                      setName={modalType}
                      isModalVisible={isPrivacyModalVisible}
                      setIsModalVisible={handleClosePrivacyModal}
                    />
                  </GlobalThemeProvider>
                </ScheduledSongsProvider>
              </CsvImportProvider>
            </PackageContextProvider>
          </PricingProvider>
        </PlaylistContextProvider>
      </LoginContextProvider>
    </div>
  );
}

export default App;
